import { describeError, useApi } from '@classgo/app-kit';
import { Button, Card, EmptyState, Table } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';

import { staffQueries } from '../../api/staff-api';
import type { ContactRequestView } from '../../api/types';
import { useT } from '../../i18n';
import { formatDateTime } from '../../lib/format';

/**
 * Messages from the landing's contact form, newest first: the rep's follow-up list until SES
 * delivers them by email.
 */
export const ContactsScreen = () => {
  const t = useT();
  const api = useApi();
  const contacts = useQuery(staffQueries.contactRequests(api));
  return (
    <Card title={t('contacts.title')} flush>
      {contacts.isError ? (
        <div className="p-6">
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, contacts.error)}
            action={
              <Button variant="secondary" onClick={() => void contacts.refetch()}>
                {t('session.retry')}
              </Button>
            }
          />
        </div>
      ) : (
        <Table<ContactRequestView>
          caption={t('contacts.caption')}
          rows={contacts.data ?? []}
          rowKey={(row) => row.id}
          loading={contacts.isPending}
          empty={
            <EmptyState title={t('contacts.empty.title')} description={t('contacts.empty.body')} />
          }
          columns={[
            {
              key: 'who',
              header: t('contacts.column.who'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">{row.name}</p>
                  <p className="text-xs text-gray-500">
                    <a
                      className="text-brand-600 underline underline-offset-2 hover:decoration-2"
                      href={`mailto:${row.email}`}
                    >
                      {row.email}
                    </a>
                    {row.phone ? ` · ${row.phone}` : ''}
                  </p>
                </div>
              ),
            },
            {
              key: 'school',
              header: t('contacts.column.school'),
              cell: (row) => row.school ?? t('common.none'),
            },
            {
              key: 'message',
              header: t('contacts.column.message'),
              cell: (row) => (
                <span className="whitespace-pre-wrap text-gray-700">{row.message}</span>
              ),
            },
            {
              key: 'when',
              header: t('contacts.column.when'),
              cell: (row) => formatDateTime(row.createdAt),
              sortable: true,
              sortValue: (row) => row.createdAt,
            },
          ]}
        />
      )}
    </Card>
  );
};
